import { createContext, useContext, useMemo } from 'react';
import type { GreasemapClient } from '@greasemap/api-client';
import { buildApiClient } from './client';
import { readAuthConfig } from '../auth/config';
import { useAuth } from '../auth/AuthProvider';

const ApiClientContext = createContext<GreasemapClient | null>(null);

/**
 * Provides a single API client to the application.
 *
 * The development identity headers and the bearer-token accessor are configured in exactly one place.
 * The accessor comes from {@link useAuth}; in development mode it returns no token and the client
 * falls back to the header scheme, so both modes flow through here. The client is rebuilt only when
 * the accessor's identity changes (e.g. MSAL finishing initialisation), which is rare.
 *
 * @param props - Component props.
 * @param props.children - The application tree.
 * @returns The provider element.
 */
export function ApiClientProvider({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const { getAccessToken } = useAuth();
  const client = useMemo(() => buildApiClient(readAuthConfig(), getAccessToken), [getAccessToken]);

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/**
 * Reads the shared API client.
 *
 * @returns The configured client.
 * @throws {Error} Called outside an {@link ApiClientProvider}.
 */
export function useApiClient(): GreasemapClient {
  const client = useContext(ApiClientContext);

  if (client === null) {
    throw new Error('useApiClient must be used inside an ApiClientProvider.');
  }

  return client;
}
